import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

import { useTranslation } from "react-i18next";
import { courseAPI } from "../services/postgresService";
import { useSettings } from "../hooks/useSettings";
import ContactPopup from "./ContactPopup";

// Floating accent motion paths (prevent undefined refs)
const float1 = [0, -12, 0];
const float2 = [0, -16, 0];
const float3 = [0, -10, 0];
const floatX1 = [0, 8, 0];
const floatX2 = [0, -10, 0];
const floatX3 = [0, 12, 0];
const floatS1 = [1, 1.08, 1];
const floatS2 = [1, 1.05, 1];
const floatS3 = [1, 1.1, 1];

const floatingShapes = [
  { y: float1, x: floatX1, scale: floatS1, className: "top-16 left-10 bg-safir-primary/15 w-32 h-32" },
  { y: float2, x: floatX2, scale: floatS2, className: "bottom-20 right-12 bg-safir-secondary/15 w-36 h-36" },
  { y: float3, x: floatX3, scale: floatS3, className: "top-1/3 right-1/4 bg-safir-primary/10 w-28 h-28" },
];

const CourseCard = ({ course, index, onEnrollClick, t }) => {
  return (
    <motion.div
      className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg hover:shadow-2xl overflow-hidden h-full flex flex-col border border-gray-100 hover:border-safir-primary/30 transition-all duration-300"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{
        y: -12,
        boxShadow:
          "0 25px 50px -12px rgba(30, 64, 175, 0.2)",
      }}
    >
      {/* Image Container with Overlay */}
      <div className="relative h-56 overflow-hidden bg-gradient-to-br from-safir-primary to-safir-secondary">
        <motion.img
          src={course.image}
          alt={course.title}
          className="w-full h-full object-cover"
          whileHover={{ scale: 1.15 }}
          transition={{ duration: 0.6 }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
        
        {/* Category Badge */}
        <div className="absolute top-4 right-4">
          <motion.span
            className="inline-block px-4 py-2 text-xs font-bold bg-white text-safir-primary rounded-full shadow-lg"
            whileHover={{ scale: 1.1 }}
          >
            {course.category}
          </motion.span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-grow flex flex-col">
        <h3 className="text-xl font-bold text-safir-dark mb-3 line-clamp-2">
          {course.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 flex-grow line-clamp-3">
          {course.description}
        </p>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 py-4 border-t border-gray-200">
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Instructor</div>
            <div className="text-sm font-semibold text-safir-dark truncate">
              {course.instructor || 'TBA'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Duration</div>
            <div className="text-sm font-semibold text-safir-dark">
              {course.duration || 'TBA'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Students</div>
            <div className="text-sm font-semibold text-safir-dark">
              {course.students || 0}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Starts</div>
            <div className="text-sm font-semibold text-safir-dark">
              {course.start_date ? new Date(course.start_date).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'}) : 'TBA'}
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <motion.button
          className="w-full mt-4 bg-gradient-to-r from-safir-primary to-safir-secondary text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onEnrollClick}
        >
          {t('courses.enrollNow')}
        </motion.button>
      </div>
    </motion.div>
  );
};

const CoursesSection = () => {
  const { theme } = useSettings();
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [showContactPopup, setShowContactPopup] = useState(false);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        setIsLoading(true);
        const data = await courseAPI.getAll();
        setCourses(data);

        // Background image is now handled by useTheme hook
      } catch (error) {
        console.error("Error loading courses:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCourses();
  }, []);

  const categories = [
    "All",
    ...new Set(courses.map((course) => course.category)),
  ];

  const filteredCourses =
    activeCategory === "All"
      ? courses
      : courses.filter((course) => course.category === activeCategory);

  return (
    <div
      className="py-20 relative"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(31, 125, 83, 0.08) 0%, rgba(37, 95, 56, 0.08) 100%), url(${theme.coverImages.courses})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
      id="courses"
    >
      {/* Floating glow accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingShapes.map((shape, idx) => (
          <motion.div
            key={idx}
            className={`absolute rounded-full blur-3xl ${shape.className}`}
            animate={{ y: shape.y, x: shape.x, scale: shape.scale }}
            transition={{ duration: 6 + idx, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('courses.title')}
          </h2>
          <motion.div
            className="w-24 h-1 bg-safir-primary mx-auto mb-6"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          ></motion.div>
          <p className="max-w-2xl mx-auto text-white">
            {t('courses.description')}
          </p>
        </motion.div>

        <div className="flex justify-center flex-wrap mb-12">
          {categories.map((category, index) => (
            <motion.button
              key={index}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 m-2 rounded-full ${
                activeCategory === category
                  ? "bg-safir-primary text-white"
                  : "bg-white text-safir-dark hover:bg-gray-200"
              } transition-colors duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-safir-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course, index) => (
              <CourseCard
                key={course.id}
                course={course}
                index={index}
                onEnrollClick={() => setShowContactPopup(true)}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
      <ContactPopup
        isOpen={showContactPopup}
        onClose={() => setShowContactPopup(false)}
      />
    </div>
  );
};

export default CoursesSection;
